import { useRef, useState } from "react";
import { Check } from "lucide-react";
import { submitInterest } from "../lib/api";

export const INTEREST_OPTIONS = [
  "Trying Commons with a real project",
  "Helping develop or test Commons",
  "Exploring collaboration",
  "Just staying in the loop",
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "");
const newSubmissionKey = () =>
  window.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

export const InterestForm = () => {
  const started = useRef(Date.now());
  // One key per form: retries of this form are recognised and not saved twice.
  const submissionKey = useRef(newSubmissionKey());
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [interests, setInterests] = useState([]);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (opt) =>
    setInterests((cur) => (cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    setState("sending");
    try {
      await submitInterest({
        ...form,
        interests,
        elapsedMs: Date.now() - started.current,
        submissionKey: submissionKey.current,
      });
      setState("done");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Something went wrong. Please try again.");
      setState("idle");
    }
  };

  if (state === "done") {
    return (
      <div className="c-interest-done" data-testid="interest-success" role="status">
        <span className="c-interest-done-icon" aria-hidden><Check size={16} /></span>
        <div>
          <div className="c-interest-done-title">Thank you.</div>
          <p>Your note has been received. We'll reach out when there's something to try together.</p>
        </div>
      </div>
    );
  }

  const sending = state === "sending";
  return (
    <form className="c-interest-form" onSubmit={onSubmit} noValidate data-testid="interest-form">
      <label className="c-interest-field">
        <span className="c-interest-label">Name <em>(optional)</em></span>
        <input className="c-interest-input" value={form.name} onChange={set("name")} maxLength={120}
          autoComplete="name" disabled={sending} data-testid="interest-name-input" />
      </label>
      <label className="c-interest-field">
        <span className="c-interest-label">Email</span>
        <input className="c-interest-input" type="email" value={form.email} onChange={set("email")}
          maxLength={254} autoComplete="email" required disabled={sending} data-testid="interest-email-input" />
      </label>
      <fieldset className="c-interest-field c-interest-fieldset">
        <legend className="c-interest-label">I'm interested in <em>(optional)</em></legend>
        {INTEREST_OPTIONS.map((opt) => (
          <label key={opt} className={`c-interest-check${interests.includes(opt) ? " is-on" : ""}`}>
            <input type="checkbox" checked={interests.includes(opt)} onChange={() => toggle(opt)}
              disabled={sending} data-testid={`interest-option-${slug(opt)}`} />
            <span>{opt}</span>
          </label>
        ))}
      </fieldset>
      <label className="c-interest-field">
        <span className="c-interest-label">Anything else? <em>(optional)</em></span>
        <textarea className="c-interest-input c-interest-textarea" value={form.message} onChange={set("message")}
          maxLength={2000} rows={4} disabled={sending} data-testid="interest-message-input" />
      </label>
      <div className="c-interest-hp" aria-hidden="true">
        <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></label>
      </div>
      {error ? <div className="c-interest-error" role="alert" data-testid="interest-error">{error}</div> : null}
      <button type="submit" className="c-btn c-btn-primary c-interest-submit" disabled={sending}
        data-testid="interest-submit-btn">
        {sending ? "Sending…" : "Send"}
      </button>
    </form>
  );
};
